<template>
  <div
    class="w-full bg-truegray-100 flex flex-col justify-center px-12 md:px-16 lg:px-32 py-28"
  >
    <h2 class="text-truegray-600 text-5xl mb-12">Latest Posts</h2>
    <div class="w-full">
      <PostsFilter class="mb-8" @submit-filters="submitFilters" />

      <!-- Loading -->
      <div
        v-if="postsLoading"
        class="w-full h-24 flex items-center justify-center"
      >
        <lottie :options="LOADING_ANIMATION_OPTIONS" />
      </div>
      <!-- /Loading -->

      <!-- Content -->
      <template v-else-if="!!posts.length">
        <!-- Posts -->
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4"
        >
          <PostsCard
            v-for="(post, i) in posts"
            :id="post.id"
            :key="i"
            :ref="'posts-' + i"
            :post="post"
            :class="
              post.category === 3 &&
              (!post.event_start_at || !post.event_end_at)
                ? 'hide-this'
                : ''
            "
          />
        </div>
        <!-- /Posts -->

        <!-- Pagination -->
        <div
          v-if="pagination.previousPage || pagination.nextPage"
          class="flex justify-between items-center h-10 mt-4"
        >
          <span
            :disabled="!pagination.previousPage"
            :class="[
              !pagination.previousPage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="getPosts(pagination.previousPage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 17l-5-5m0 0l5-5m-5 5h12"
              />
            </svg>
          </span>
          <span class="text-center text-xs">
            Page {{ pagination.currentPage }} of
            {{ pagination.lastPage }}
          </span>
          <span
            :disabled="!pagination.nextPage"
            :class="[
              !pagination.nextPage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="getPosts(pagination.nextPage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </span>
        </div>
        <!-- /Pagination -->
      </template>
      <div v-else>
        <span class="italic text-md text-truegray-600">No posts</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from '../../../js/store/index.js'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import PostsCard from '../../../shared/components/PostsCard.vue'
import PostsFilter from './PostsFilter.vue'

export default defineComponent({
  name: 'Posts',
  components: { PostsCard, PostsFilter },

  setup() {
    const LOADING_ANIMATION_OPTIONS = {
      animationData: loadingAnimation.default,
    }
    const store = useStore()

    const postsLoading = ref(false)
    const posts = computed(
      () => store.getters['contributors/getOrganisationProfilePosts'],
    )
    const filters = ref(null)
    const pagination = computed(
      () => store.getters['contributors/getOrganisationProfilePostsPagination'],
    )

    const submitFilters = async (payload, callback) => {
      filters.value = payload
      await getPosts()
      callback()
    }

    const getPosts = async (page) => {
      postsLoading.value = true
      await store.dispatch('contributors/dispatchUpdateProfilePosts', {
        page,
        filters: filters.value,
      })
      postsLoading.value = false
    }

    return {
      LOADING_ANIMATION_OPTIONS,
      postsLoading,
      posts,
      pagination,
      submitFilters,
      getPosts,
    }
  },
})
</script>
